import {
  ageOn,
  earliestCollected,
  formatAge,
  type ReportContent,
  SALUTATIONS,
  type Sex,
  salutationMismatch,
} from '@quickreport/domain';
import { Field, Input, Select } from '@quickreport/ui';
import { Plus, X } from 'lucide-react';
import { SamplesEditor } from './SamplesEditor';

/** Patient and visit details as they will print. Editing here changes this report only. */
export function DetailsPanel({
  content,
  editable,
  onChange,
}: {
  content: ReportContent;
  editable: boolean;
  onChange: (c: ReportContent) => void;
}) {
  const p = content.patient;
  const v = content.visit;
  const setP = (patch: Partial<typeof p>) => onChange({ ...content, patient: { ...p, ...patch } });
  const setV = (patch: Partial<typeof v>) => onChange({ ...content, visit: { ...v, ...patch } });
  const warn = salutationMismatch(p.salutation as never, p.sex);
  const local = (iso?: string) => (iso ? toLocalInput(iso) : '');

  return (
    <fieldset
      disabled={!editable}
      className="grid min-w-0 gap-x-4 gap-y-3 border-0 p-4 md:grid-cols-2 xl:grid-cols-4"
    >
      {content.kind === 'generic' ? (
        <Field label="Title (report name)" className="md:col-span-2 xl:col-span-4">
          <Input
            value={content.title ?? ''}
            onChange={(e) => onChange({ ...content, title: e.target.value })}
          />
        </Field>
      ) : null}
      <div className="grid grid-cols-[6rem_1fr] gap-3 md:col-span-2">
        <Field label="Title">
          <Select
            value={p.salutation ?? ''}
            onChange={(e) => setP({ salutation: e.target.value || undefined })}
          >
            <option value="" />
            {SALUTATIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Patient name">
          <Input value={p.name} onChange={(e) => setP({ name: e.target.value })} />
        </Field>
      </div>
      {warn ? (
        <p
          role="alert"
          className="rounded-md bg-amber-50 px-3 py-1.5 text-[13px] text-warn md:col-span-2 xl:col-span-4"
        >
          {warn}. Check the title and sex.
        </p>
      ) : null}
      <div className="contents">
        <Field label="Sex">
          <Select
            value={p.sex ?? ''}
            onChange={(e) => setP({ sex: (e.target.value || undefined) as Sex | undefined })}
          >
            <option value="" />
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="O">Other</option>
          </Select>
        </Field>
        <Field label="Age" hint="As printed, e.g. 36 Y, 8 M, 12 D">
          <Input
            value={p.ageText}
            onChange={(e) => {
              const m = /^\s*(\d+)\s*([YMDymd])/.exec(e.target.value);
              const days = m
                ? Math.round(
                    Number(m[1]) *
                      (({ Y: 365.25, M: 30.4375, D: 1 } as Record<string, number>)[
                        (m[2] as string).toUpperCase()
                      ] ?? 1),
                  )
                : undefined;
              setP({ ageText: e.target.value, ...(days !== undefined ? { ageDays: days } : {}) });
            }}
          />
        </Field>
        <Field label="Mobile">
          <Input
            value={p.phone ?? ''}
            onChange={(e) => setP({ phone: e.target.value || undefined })}
          />
        </Field>
        <Field label="Email">
          <Input
            value={p.email ?? ''}
            onChange={(e) => setP({ email: e.target.value || undefined })}
          />
        </Field>
      </div>
      <Field label="Address" className="md:col-span-2">
        <Input
          value={p.address ?? ''}
          onChange={(e) => setP({ address: e.target.value || undefined })}
        />
      </Field>
      <div className="contents">
        <Field label="Patient ID">
          <Input
            value={p.externalId ?? ''}
            onChange={(e) => setP({ externalId: e.target.value || undefined })}
          />
        </Field>
        <Field label="Referred by">
          <Input
            value={v.referredBy ?? ''}
            onChange={(e) => setV({ referredBy: e.target.value || undefined })}
          />
        </Field>
      </div>
      <div className="md:col-span-2 xl:col-span-4">
        <SamplesEditor
          samples={content.samples}
          disabled={!editable}
          onChange={(samples) => {
            // The collected time follows the earliest sample, so lists sort on it.
            const first = earliestCollected(samples);
            onChange({
              ...content,
              samples,
              visit: { ...v, ...(first ? { collectedAt: first } : {}) },
            });
          }}
        />
      </div>
      {content.samples.length === 0 ? (
        <Field label="Sample (older entry)">
          <Input
            value={v.sampleType ?? ''}
            onChange={(e) => setV({ sampleType: e.target.value || undefined })}
          />
        </Field>
      ) : null}
      <div className="contents">
        {content.samples.length === 0 ? (
          <Field label="Collected">
            <Input
              type="datetime-local"
              value={local(v.collectedAt)}
              onChange={(e) =>
                setV({
                  collectedAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                })
              }
            />
          </Field>
        ) : null}
        <Field label="Received">
          <Input
            type="datetime-local"
            value={local(v.receivedAt)}
            onChange={(e) =>
              setV({
                receivedAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              })
            }
          />
        </Field>
        <Field
          label="Reported"
          hint={
            v.reportedAtManual || !v.reportedAt
              ? 'Leave empty to use the time of signing'
              : 'Set when it was signed'
          }
        >
          <Input
            type="datetime-local"
            value={v.reportedAtManual || !editable ? local(v.reportedAt) : ''}
            onChange={(e) =>
              e.target.value
                ? setV({
                    reportedAt: new Date(e.target.value).toISOString(),
                    reportedAtManual: true,
                  })
                : setV({ reportedAt: undefined, reportedAtManual: undefined })
            }
          />
        </Field>
      </div>

      <section className="grid gap-2 md:col-span-2 xl:col-span-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[13px] font-semibold">More details</h3>
          {editable ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[13px] text-brand hover:underline"
              onClick={() =>
                onChange({ ...content, fields: [...content.fields, { label: '', value: '' }] })
              }
            >
              <Plus size={13} /> Add field
            </button>
          ) : null}
        </div>
        {content.fields.length === 0 ? (
          <p className="text-[12px] text-slate">
            Extra lines that print with the patient details, e.g. Doctor, Ward, Insurance no.
          </p>
        ) : null}
        {content.fields.map((f, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fields have no ids
          <div key={i} className="grid grid-cols-[1fr_1.4fr_auto] gap-2">
            <Input
              aria-label="Label"
              placeholder="Label"
              value={f.label}
              onChange={(e) =>
                onChange({
                  ...content,
                  fields: content.fields.map((x, j) =>
                    j === i ? { ...x, label: e.target.value } : x,
                  ),
                })
              }
            />
            <Input
              aria-label="Value"
              placeholder="Value"
              value={f.value}
              onChange={(e) =>
                onChange({
                  ...content,
                  fields: content.fields.map((x, j) =>
                    j === i ? { ...x, value: e.target.value } : x,
                  ),
                })
              }
            />
            <button
              type="button"
              aria-label="Remove field"
              className="px-1 text-slate hover:text-flag"
              onClick={() =>
                onChange({ ...content, fields: content.fields.filter((_, j) => j !== i) })
              }
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </section>
    </fieldset>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export { ageOn, formatAge };
